import React, { useEffect, useState, useRef } from 'react';
import { Trophy, X, Star, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AchievementDefinition, AchievementCategory } from '@/models/Achievement';
import Confetti from 'react-confetti';

interface AchievementNotificationProps {
  achievement: AchievementDefinition;
  onDismiss: () => void;
  isLastAchievement?: boolean;
}

export const AchievementNotification: React.FC<AchievementNotificationProps> = ({
  achievement,
  onDismiss,
  isLastAchievement = true
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Animate in the notification after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Reset state and log when achievement changes
  useEffect(() => {
    console.log(`🏆 [AchievementNotification] Rendering notification for: ${achievement.id}`);
    setIsVisible(true);
    setShowConfetti(false);
  }, [achievement.id]);

  // No auto-dismiss - notifications stay until user action

  const handleDismiss = () => {
    // Start by showing confetti while keeping the card fully visible
    console.log('🎉 [AchievementNotification] Showing confetti celebration!');
    setShowConfetti(true);
    
    // Keep the card visible for 1.5 seconds while confetti plays
    // Only start fading the card out after this delay
    setTimeout(() => {
      console.log('🎉 [AchievementNotification] Starting gradual fade-out animation');
      setIsVisible(false);
    }, 1500);
    
    // After the confetti animation (2.5 seconds total duration)
    // then call onDismiss to remove the component
    setTimeout(() => {
      console.log('🎉 [AchievementNotification] Completing dismissal');
      onDismiss();
    }, 3000);
  };
  
  // Get icon based on achievement category
  const getIconForCategory = () => {
    switch (achievement.category) {
      case "xp" as AchievementCategory:
        return <Star className="h-6 w-6 text-yellow-500" />;
      case "streak" as AchievementCategory:
        return <Trophy className="h-6 w-6 text-yellow-500" />;
      case "perfect" as AchievementCategory:
        return <Trophy className="h-6 w-6 text-yellow-500" />;
      case "daily" as AchievementCategory:
        return <Star className="h-6 w-6 text-blue-500" />;
      default:
        return <Trophy className="h-6 w-6 text-primary" />;
    }
  };
  
  // Get color class based on achievement tier
  const getTierColorClass = () => {
    switch (achievement.tier) {
      case 1:
        return 'bg-gradient-to-r from-bronze-500 to-bronze-400';
      case 2:
        return 'bg-gradient-to-r from-silver-600 to-silver-400';
      case 3:
        return 'bg-gradient-to-r from-gold-600 to-gold-400';
      default:
        return 'bg-gradient-to-r from-primary to-primary-foreground';
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`fixed inset-0 flex items-center justify-center p-4 bg-black/50 z-50 transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      onClick={(e) => {
        // Close on background click
        if (e.target === e.currentTarget) {
          handleDismiss();
        }
      }}
    >
      {showConfetti && (
        <Confetti
          key={`confetti-${achievement.id}`}
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={800}
          gravity={0.15}
          tweenDuration={2500}
          initialVelocityY={15}
          confettiSource={{
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
            w: 0,
            h: 0
          }}
          colors={['#FFD700', '#FFA500', '#FF4500', '#9370DB', '#00CED1', '#32CD32', '#FF1493']}
          drawShape={ctx => {
            ctx.beginPath();
            ctx.arc(0, 0, 6, 0, 2 * Math.PI);
            ctx.fill();
          }}
        />
      )}
      <Card 
        key={`achievement-card-${achievement.id}`}
        className={`w-full max-w-sm transform transition-all duration-1500 ${isVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-20'}`}
      >
        <CardHeader className={`${getTierColorClass()} text-white`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {getIconForCategory()}
              <span className="font-bold uppercase text-xs">Achievement Unlocked!</span>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardTitle className="text-xl">{achievement.title}</CardTitle>
        </CardHeader>
        
        <CardContent className="pt-4">
          <p className="text-muted-foreground">{achievement.description}</p>
          
          {achievement.xpReward && achievement.xpReward > 0 && (
            <div className="mt-4 flex items-center gap-2 text-yellow-600">
              <Star className="h-5 w-5 fill-yellow-400 stroke-yellow-600" />
              <span className="font-semibold">+{achievement.xpReward} XP bonus!</span>
            </div>
          )}
        </CardContent>
        
        <CardFooter className="flex justify-center">
          <Button 
            onClick={handleDismiss}
            className="flex items-center gap-1 px-8"
            variant="default"
            size="lg"
          >
            Celebrate
            <PartyPopper className="h-5 w-5 ml-1" />
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
